import moment from "moment";
import React, { useEffect, useState } from "react";
import { Link, useHistory, useParams } from "react-router-dom";

import BooleanIcon from "veneer/BooleanIcon";
import Button from "veneer/Button";
import DataTable, {
  createColumnHelper,
  createFilter,
  sorting,
} from "veneer/DataTable";

import { useAppDispatch, useAppSelector } from "cortex/ui/src/hooks";
import {
  FormAction,
  Network,
  VistarNetwork,
} from "cortex/ui/src/network/model";
import { createPromiseApiAction } from "cortex/ui/src/redux-store/api-sagas";
import {
  getAuthUser,
  getVistarAccountIds,
} from "cortex/ui/src/redux-store/auth/selectors";
import {
  networksSelector,
  networksStoresLoadedSelector,
} from "cortex/ui/src/redux-store/networks/selectors";
import { makeGetAllAsArray } from "cortex/ui/src/redux-store/selectors";
import {
  isAdmin,
  isVenueSyncEnabled,
} from "cortex/ui/src/redux-store/users/selectors";
import { LOAD_VISTAR_ACCOUNT_NETWORKS } from "cortex/ui/src/redux-store/vistar-accounts/actions";
import { LoadingIndicator } from "cortex/ui/src/ui/loader";
import { Container } from "cortex/ui/src/view/layout";

import NetworkCreateDrawer from "./NetworkCreateDrawer";

const getAllNetworksAsArray = makeGetAllAsArray<Network>();
const basePath = "/networks";
const filter = createFilter<Network>("name");
const column = createColumnHelper<Network>();

const columns = [
  column.accessor("name", {
    header: "Name",
    cell: (prop) => {
      const value = prop.getValue();
      return <Link to={`${basePath}/${prop.row.original.id}`}>{value}</Link>;
    },
    meta: {
      primary: true,
    },
    sortingFn: "alphanumeric",
  }),
  column.accessor("num_devices", {
    header: "Device Count",
    cell: (prop) => prop.getValue(),
    sortingFn: sorting.number((row) => row.num_devices),
  }),
  column.accessor("uptime", {
    header: "Uptime (yesterday)",
    cell: (prop) => {
      const value = prop.getValue() ? prop.getValue() : 0;
      return `${value.toFixed(2)}%`;
    },
    sortingFn: sorting.number((row) => row.uptime),
  }),
  column.accessor("device_update_disabled", {
    header: "Remote Updates",
    cell: (prop) => {
      return <BooleanIcon value={!prop.getValue()} />;
    },
    sortingFn: sorting.boolean((row) => !row.device_update_disabled),
  }),
  column.accessor("device_restart_time", {
    header: "Restart Schedule",
    cell: (prop) => {
      const value = prop.getValue();
      if (value) {
        if (value.startsWith("*")) {
          const t = moment(value, "*-H");
          if (t.isValid()) {
            return "Every day, " + t.format("HH:00");
          }
        }

        return moment(value, "E-H").format("dddd, HH:00");
      }
      return "None";
    },
    sortingFn: "alphanumeric",
  }),
  column.accessor("is_sandbox", {
    header: "Sandbox",
    cell: (prop) => {
      return <BooleanIcon value={prop.getValue()} />;
    },
    sortingFn: sorting.boolean((row) => row.is_sandbox),
  }),
  column.accessor("system_managed", {
    header: "Ad Platform Synced",
    cell: (prop) => {
      return <BooleanIcon value={prop.getValue()} />;
    },
    sortingFn: sorting.boolean((row) => row.system_managed),
  }),
];

export default function NetworkList() {
  const { action } = useParams<{ action: FormAction }>();
  const dispatch = useAppDispatch();
  const history = useHistory();
  const networksObject = useAppSelector(networksSelector);
  const networks = getAllNetworksAsArray({ byId: networksObject });
  const vistarAccountIds: ReadonlyArray<number> | null =
    useAppSelector(getVistarAccountIds);
  const storesLoaded = useAppSelector(networksStoresLoadedSelector);
  const user = useAppSelector(getAuthUser);
  const userIsAdmin = isAdmin(user);
  const venueSyncEnabled = isVenueSyncEnabled(user);
  const [vistarNetworks, setVistarNetworks] = useState<
    readonly VistarNetwork[] | undefined
  >(undefined);

  useEffect(() => {
    if (!userIsAdmin && action) {
      history.replace(basePath);
    }
  }, [userIsAdmin, action, history]);

  useEffect(() => {
    if (!vistarAccountIds || vistarAccountIds.length === 0) {
      setVistarNetworks([]);
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        type VistarAccountNetworksResponse = {
          data?: {
            data?: ReadonlyArray<VistarNetwork>;
          };
        };
        const response = (await createPromiseApiAction(
          dispatch,
          LOAD_VISTAR_ACCOUNT_NETWORKS,
        )) as VistarAccountNetworksResponse;
        const networks: readonly VistarNetwork[] = response.data?.data ?? [];
        if (!cancelled) setVistarNetworks(networks);
      } catch (err) {
        console.error("Error loading vistar networks", err);
        if (!cancelled) setVistarNetworks([]);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [dispatch, vistarAccountIds]);

  const showCreateDrawer = userIsAdmin && action === FormAction.CREATE;

  const handleCloseDrawer = () => {
    history.push(basePath);
  };

  const addButton = userIsAdmin ? (
    <Link to="/networks/create">
      <Button>Add Network</Button>
    </Link>
  ) : null;

  return (
    <Container title="Networks">
      {storesLoaded ? (
        <DataTable
          columns={columns}
          filter={filter}
          hideColumns={!venueSyncEnabled ? ["system_managed"] : []}
          initialPageSize={10}
          initialSort={{ id: "num_devices", desc: true }}
          primaryActions={addButton}
          rows={networks}
        />
      ) : (
        <LoadingIndicator />
      )}
      {showCreateDrawer && (
        <NetworkCreateDrawer
          onCancel={handleCloseDrawer}
          open={showCreateDrawer}
          vistarNetworks={vistarNetworks}
        />
      )}
    </Container>
  );
}
